import type { FC, ReactNode } from 'react'
import * as React from 'react'
import { BasicErrorBoundary } from '@w/utils-fe'
import { BugIcon } from '../components/icon'
import { Button } from '../components/button'
import { Shape } from '../components/shape'
import { cookie6 } from '../components/shapes'
import { serializeError } from '@w/utils'
import { useErrorBoundaryDomain } from '../hooks/use-error-boundary-domain'
import { ErrorDetails } from '../components/error-details'
import { useTranslation } from '../i18n'

interface ErrorBoundaryProps {
  fallback?:
    // oxlint-disable-next-line typescript/no-redundant-type-constituents -- unknown | null is intentional for error boundary API
    FC<{ error: unknown | null; resetErrorBoundary?: () => void }> | ReactNode
  children: ReactNode
  resetKeys?: unknown[]
  onReset?: () => void
  onError?: (error: Error) => void
}

export function ErrorBoundary({
  children,
  resetKeys,
  onReset,
  onError,
  ...props
}: ErrorBoundaryProps) {
  const FallbackComponent:
    // oxlint-disable-next-line typescript/no-redundant-type-constituents -- unknown | null is intentional for error boundary API
    FC<{ error: unknown | null; resetErrorBoundary?: () => void }> | ReactNode =
    props.fallback ?? ErrorFallback

  const fallback =
    typeof FallbackComponent === 'function'
      ? ({
          error,
          resetErrorBoundary,
        }: {
          error: Error
          resetErrorBoundary?: () => void
        }) => (
          <FallbackComponent
            error={error}
            resetErrorBoundary={resetErrorBoundary}
          />
        )
      : () => FallbackComponent as React.ReactNode

  return (
    <BasicErrorBoundary
      fallback={fallback}
      resetKeys={resetKeys}
      onReset={onReset}
      onError={onError}
    >
      {children}
    </BasicErrorBoundary>
  )
}

interface ErrorFallbackProps {
  // oxlint-disable-next-line typescript/no-redundant-type-constituents -- unknown | null is intentional for error boundary API
  error?: unknown | null
  title?: string
  description?: string
  isMinimal?: boolean
  onReload?: () => void
}


export function ErrorFallback({
  title,
  description,
  isMinimal,
  onReload,
  ...props
}: ErrorFallbackProps) {
  const { t } = useTranslation('ui')
  const { domainText } = useErrorBoundaryDomain(props.error)
  const [copyState, setCopyState] = React.useState<'idle' | 'copied' | 'failed'>('idle')
  async function copyDetails() {
    try {
      await navigator.clipboard.writeText(JSON.stringify(serializeError(props.error), null, 2))
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
  }
  if (isMinimal) {
    return (
      <div className="flex flex-col items-center justify-center px-4 text-center">
        <ErrorDetails error={props.error} title={title} description={description} compact />
      </div>
    )
  }

  return (
    <div className="bg-surface-1 text-on-surface flex min-h-svh w-full min-w-0 flex-col items-center px-6 py-12 sm:py-16">
      <div className="my-auto flex w-full min-w-0 max-w-xl flex-col gap-8">
        <div className="flex flex-col items-center gap-5 text-center">
          <div className="relative flex size-24 items-center justify-center" aria-hidden="true">
            <Shape shape={cookie6} className="size-full text-surface-error" />
            <BugIcon size={44} strokeWidth={1.5} className="text-error absolute" />
          </div>
          <div className="flex flex-col gap-3">
            <h1 className="text-error text-2xl font-semibold text-balance">{title ?? t('platform:error.generic.title')}</h1>
            <p className="text-on-surface-secondary text-base leading-relaxed text-pretty [overflow-wrap:anywhere]">
              {description ?? domainText ?? t('platform:error.recoveryDescription')}
            </p>
          </div>
        </div>
        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <Button variant="filled" onClick={() => { if (onReload) onReload(); else window.location.reload() }}>{t('platform:error.reload')}</Button>
          <Button variant="outlined" onClick={() => { void copyDetails() }}>{copyState === 'copied' ? t('platform:error.copied') : t('platform:error.copy')}</Button>
        </div>
        {copyState !== 'idle' && <p role="status" className="text-on-surface-secondary text-center text-sm">
          {copyState === 'failed' ? t('platform:error.copyFailed') : t('platform:error.copySuccess')}
        </p>}
        <div className="border-border min-w-0 border-t pt-2">
          <ErrorDetails error={props.error} diagnosticsOnly />
        </div>
      </div>
    </div>
  )
}

export function ErrorFallbackMinimal(
  props: Omit<ErrorFallbackProps, 'isMinimal'>,
) {
  return <ErrorFallback {...props} isMinimal />
}
